import { useEffect, useState } from "react";
import { isAbortError, queryReport } from "../api/newReportingClient";
import { ReportResponse } from "../api/types";
import { ReportFilterValue } from "../components/NewReportFilters";
import { toQueryParams } from "./filters";
import { paymentBreakdownFromFinancial } from "./tenderBreakdown";

export interface VerticalSalesState {
  financial: ReportResponse | null; // FinancialByDimension(Event) — the event's financials
  // Per-tender breakdown DERIVED from financial's capped Tender* totals ("Gross <Tender> Sales" —
  // reconciles to the Gross line, like the old report's Payment Breakdown). Deliberately NOT
  // PaymentMix: that report is money COLLECTED (tips + service fee included) and must never sit
  // under the Gross/Net summary as if it tied to Gross.
  paymentBreakdown: ReportResponse | null;
  cardBrand: ReportResponse | null; // approved card tenders by brand (money collected — matches old)
  productMix: ReportResponse | null; // ProductMixByItem
  loading: boolean;
  error: string | null;
}

const EMPTY: VerticalSalesState = {
  financial: null,
  paymentBreakdown: null,
  cardBrand: null,
  productMix: null,
  loading: false,
  error: null,
};

// Vertical Sales composes 3 event-scoped calls in parallel (the payment breakdown is derived from
// the financial response). Idle until an event is picked (pass `null` to stay idle before Search).
// All requests share one AbortController, so a change (or unmount) aborts the whole batch
// server-side rather than letting it run stale.
export function useVerticalSales(filters: ReportFilterValue | null): VerticalSalesState {
  const [state, setState] = useState<VerticalSalesState>(EMPTY);
  const eventId = filters?.eventId;
  const key = filters
    ? JSON.stringify({
        eventId,
        from: filters.from,
        to: filters.to,
        vendorId: filters.vendorId ?? "",
      })
    : null;

  useEffect(() => {
    if (!filters || !eventId) {
      setState(EMPTY);
      return;
    }
    const controller = new AbortController();
    const { signal } = controller;
    setState((s) => ({ ...s, loading: true, error: null }));

    Promise.all([
      queryReport(toQueryParams("FinancialByDimension", filters, { dimension: "Event" }), signal),
      queryReport(toQueryParams("CardBrandBreakdown", filters), signal),
      queryReport(toQueryParams("ProductMixByItem", filters, { limit: 1000 }), signal),
    ])
      .then(([financial, cardBrand, productMix]) => {
        if (!signal.aborted)
          setState({
            financial,
            paymentBreakdown: paymentBreakdownFromFinancial(financial),
            cardBrand,
            productMix,
            loading: false,
            error: null,
          });
      })
      .catch((err: unknown) => {
        if (signal.aborted || isAbortError(err)) return; // cancelled — leave newer state alone
        setState({
          ...EMPTY,
          error: err instanceof Error ? err.message : "Failed to load vertical sales",
        });
      });

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
}
